import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { CARGO_LABELS, CARGO_LIST } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BellPlus } from "lucide-react";

// Lets a user create a transport-request alert when no route is found.
// On creation the backend emails carriers with a matching route about the opportunity.
export function AlertDialog({ prefill = {}, trigger, onCreated }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [origin, setOrigin] = useState(prefill.origin || "");
  const [destination, setDestination] = useState(prefill.destination || "");
  const [cargoType, setCargoType] = useState(prefill.cargo_type && prefill.cargo_type !== "all" ? prefill.cargo_type : "");
  const [dateFrom, setDateFrom] = useState(prefill.date_from || "");
  const [cargoValue, setCargoValue] = useState("");
  const [notes, setNotes] = useState("");

  const openChange = (v) => {
    if (v && !user) { toast.info("Accedi per creare un alert"); navigate("/accedi"); return; }
    setOpen(v);
  };

  const submit = async () => {
    if (!origin || !destination) { toast.error("Inserisci origine e destinazione"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/alerts", {
        origin, destination,
        cargo_type: cargoType || null,
        date_from: dateFrom || null,
        cargo_value: cargoValue ? Number(cargoValue) : 0,
        notes: notes || null,
      });
      const n = data.carriers_notified || 0;
      toast.success(n > 0 ? `Alert creato! ${n} trasportatore${n > 1 ? "i" : ""} notificat${n > 1 ? "i" : "o"} via email.` : "Alert creato! Ti avviseremo appena una tratta sarà disponibile.");
      setOpen(false);
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={openChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-display">Crea un alert di trasporto</DialogTitle>
          <DialogDescription>Inserisci i dettagli: avviseremo i trasportatori con tratte compatibili e ti notificheremo appena disponibile.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Origine *</Label><Input value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="es. Milano" className="rounded-sm mt-1" data-testid="alert-origin-input" /></div>
            <div><Label className="text-xs">Destinazione *</Label><Input value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="es. Napoli" className="rounded-sm mt-1" data-testid="alert-destination-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Tipo merce</Label>
              <Select value={cargoType} onValueChange={setCargoType}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="alert-cargo-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{CARGO_LIST.map((c) => <SelectItem key={c} value={c}>{CARGO_LABELS[c]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Data desiderata</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-sm mt-1" data-testid="alert-date-input" /></div>
          </div>
          <div><Label className="text-xs">Valore merce (€)</Label><Input type="number" value={cargoValue} onChange={(e) => setCargoValue(e.target.value)} placeholder="per il preventivo polizza" className="rounded-sm mt-1" data-testid="alert-value-input" /></div>
          <div><Label className="text-xs">Dettagli del trasporto</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Peso, dimensioni, note utili…" className="rounded-sm mt-1" data-testid="alert-notes-input" /></div>
        </div>

        <DialogFooter>
          <Button onClick={submit} disabled={loading} className="rounded-sm bg-accent hover:bg-accent/90 text-accent-foreground font-semibold w-full gap-2" data-testid="alert-submit-btn">
            <BellPlus size={16} /> {loading ? "Creazione…" : "Crea alert e avvisa i trasportatori"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
